import {
  AuthService as AuthServiceGenerated,
  UsersService,
  PasswordAuthRecoveryService,
  RoleName,
  UserStatus,
  FeedbackHrOneOnOneService,
  FeedbackLawyerConsultationService,
  FeedbackApplicationStatus,
  CreateUserFeedbackRequest,
  HiringReferralsService,
  HiringDirectionsService,
  ReferralApplicationStatus,
  UpdateRecruitingDirectionRequest,
  VacationApplicationsService,
  DayOffApplicationsService,
  VacationApplicationStatus,
  VacationApplicationType,
  DayOffApplicationStatus,
  DayOffApplicationType,
  SickLeaveApplicationsService,
  SickLeaveApplicationStatus,
  CreateMySickLeaveApplicationRequest,
  UpdateSickLeaveEndDateRequest,
  UpdateVacationApplicationDatesRequest,
  FillUserProfileRequest,
  CreateUserWithPasswordRequest,
  CreateUserReferralApplicationRequest,
  CreateMyVacationApplicationRequest,
  CreateMyDayOffApplicationRequest,
  UploadVacationOrderRequest,
  UploadVacationSignedOrderRequest,
  UploadMyVacationSignedOrderRequest,
  RescheduleMyVacationRequest,
  UploadDayOffOrderRequest,
  UploadDayOffSignedOrderRequest,
  UploadMyDayOffSignedOrderRequest,
  Body_upload_my_resume_api_hiring_referrals_upload_temp_resume_post,
  Body_upload_temp_file_api_vacation_applications_upload_temp_post,
  Body_upload_temp_file_api_vacation_day_off_applications_upload_temp_post,
  ResumeType,
} from '../api-client';
import apiWrapper from './apiWrapper';
import { authService } from './authService';
import { ApiError } from '../api-client/core/ApiError';

/**
 * Глобальный обработчик 401 ошибок
 * @param error - Ошибка для обработки
 */
function handleGlobalAuthError(error: unknown): void {
  if (error instanceof ApiError && error.status === 401) {
    // Токен истёк или недействителен - удаляем его
    authService.removeToken();

    // Сохраняем текущий URL для редиректа
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/login') {
      authService.setReturnUrl(currentPath);
      // Редирект на страницу авторизации с query-параметром
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
    } else {
      // Редирект на страницу авторизации без query-параметра
      window.location.href = '/login';
    }
  }
}

// Вспомогательная функция для безопасного преобразования строки в UserStatus
function stringToUserStatus(status: string | undefined): UserStatus | undefined {
  if (!status) return undefined;
  const validStatuses = Object.values(UserStatus) as string[];
  if (validStatuses.includes(status)) {
    return status as UserStatus;
  }
  return undefined;
}

// Вспомогательная функция для безопасного преобразования строки в RoleName
function stringToRoleName(roleName: string | undefined): RoleName | undefined {
  if (!roleName) return undefined;
  const validRoles = Object.values(RoleName) as string[];
  if (validRoles.includes(roleName)) {
    return roleName as RoleName;
  }
  return undefined;
}

// Сервис для работы с аутентификацией
class AuthApiService {
  async signIn(login: string, password: string) {
    try {
      const response = await AuthServiceGenerated.signInApiAuthSignInPost({
        login,
        password,
      });

      // При успешной аутентификации сохраняем токен
      if (response && response.data && response.data.token) {
        authService.setToken(response.data.token);
        // Обновляем конфигурацию API с новым токеном
        apiWrapper.updateConfig();
      }

      return response;
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при входе:', error);
      throw error;
    }
  }

  async initRegistration(email: string) {
    try {
      return await AuthServiceGenerated.initRegistrationEndpointApiAuthRegisterPost({
        email,
      });
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при инициализации регистрации:', error);
      throw error;
    }
  }

  async completeRegistration(token: string, code: string, fullName: string) {
    try {
      const response =
        await AuthServiceGenerated.completeRegistrationEndpointApiAuthRegisterCompletePost({
          token,
          code,
          full_name: fullName,
        });

      if (response && response.data && response.data.token) {
        authService.setToken(response.data.token);
        apiWrapper.updateConfig();
      }

      return response;
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при завершении регистрации:', error);
      throw error;
    }
  }
}

// Сервис для работы с пользователями
class UsersApiService {
  async getUsers(
    limit?: number,
    offset?: number,
    email?: string,
    username?: string,
    fullName?: string,
    status?: string,
    roleName?: string
  ) {
    try {
      return await UsersService.searchUsersApiUsersGet(
        email,
        username,
        fullName,
        stringToUserStatus(status),
        stringToRoleName(roleName),
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении пользователей:', error);
      throw error;
    }
  }

  async getUserById(userId: string) {
    try {
      return await UsersService.getUserApiUsersUserIdGet(userId);
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении пользователя:', error);
      throw error;
    }
  }

  async createUser(
    email: string,
    username: string,
    password: string,
    roles?: RoleName[],
    profile?: FillUserProfileRequest
  ) {
    try {
      const requestBody: CreateUserWithPasswordRequest = {
        email,
        username,
        roles: roles || [RoleName.USER],
        password,
        profile,
      };
      return await UsersService.createUserEndpointApiUsersPost(requestBody);
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при создании пользователя:', error);
      throw error;
    }
  }

  async assignRoleToUser(userId: string, roleName: RoleName) {
    try {
      return await UsersService.assignRoleToUserApiUsersUserIdRolesPost(userId, {
        role_name: roleName,
      });
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при назначении роли пользователю:', error);
      throw error;
    }
  }

  async removeRoleFromUser(userId: string, roleName: RoleName) {
    try {
      return await UsersService.removeRoleFromUserApiUsersUserIdRolesRoleNameDelete(
        userId,
        roleName
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при удалении роли у пользователя:', error);
      throw error;
    }
  }

  async blockUser(userId: string) {
    try {
      return await UsersService.blockUserEndpointApiUsersUserIdBlockPost(userId);
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при блокировке пользователя:', error);
      throw error;
    }
  }

  async updateUserStatus(userId: string, status: UserStatus) {
    try {
      return await UsersService.updateUserStatusApiUsersUserIdStatusPut(userId, { status });
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при обновлении статуса пользователя:', error);
      throw error;
    }
  }

  async addPasswordAuth(userId: string) {
    try {
      return await UsersService.addPasswordAuthEndpointApiUsersUserIdPasswordAuthPost(userId);
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при добавлении аутентификации через пароль:', error);
      throw error;
    }
  }

  async fillUserProfile(userId: string, fullName: string) {
    try {
      const requestBody: FillUserProfileRequest = { full_name: fullName };
      return await UsersService.fillUserProfileEndpointApiUsersUserIdProfilePost(
        userId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при заполнении профиля пользователя:', error);
      throw error;
    }
  }

  async changePassword(currentPassword: string, newPassword: string, newPasswordConfirm: string) {
    try {
      return await UsersService.changePasswordAuthEndpointApiUsersMePasswordAuthChangePost({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      });
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при смене пароля:', error);
      throw error;
    }
  }
}

// Сервис для работы с заявками на обратную связь HR 1-on-1
class FeedbackHrOneOnOneApiService {
  async getApplications(
    limit?: number,
    offset?: number,
    status?: string,
    userId?: string,
    userSearch?: string
  ) {
    try {
      return await FeedbackHrOneOnOneService.listHrOneOnOneApplicationsApiFeedbackHrOneOnOneGet(
        status as FeedbackApplicationStatus | null,
        userId,
        userSearch,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявок HR 1-on-1:', error);
      throw error;
    }
  }

  async getApplication(applicationId: string) {
    try {
      return await FeedbackHrOneOnOneService.getHrOneOnOneApplicationApiFeedbackHrOneOnOneApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявки HR 1-on-1:', error);
      throw error;
    }
  }

  async updateStatus(applicationId: string, status: FeedbackApplicationStatus) {
    try {
      return await FeedbackHrOneOnOneService.updateHrOneOnOneApplicationStatusApiFeedbackHrOneOnOneApplicationIdStatusPut(
        applicationId,
        { status }
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при обновлении статуса заявки HR 1-on-1:', error);
      throw error;
    }
  }

  async createMyApplication(description?: string) {
    try {
      const requestBody: CreateUserFeedbackRequest = { description: description || null };
      return await FeedbackHrOneOnOneService.createMyHrOneOnOneApplicationApiFeedbackHrOneOnOneMePost(
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при создании моей заявки HR 1-on-1:', error);
      throw error;
    }
  }

  async getMyApplications(status?: string, limit?: number, offset?: number) {
    try {
      return await FeedbackHrOneOnOneService.listMyHrOneOnOneApplicationsApiFeedbackHrOneOnOneMeGet(
        status as FeedbackApplicationStatus | null,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моих заявок HR 1-on-1:', error);
      throw error;
    }
  }

  async getMyApplication(applicationId: string) {
    try {
      return await FeedbackHrOneOnOneService.getMyHrOneOnOneApplicationApiFeedbackHrOneOnOneMeApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моей заявки HR 1-on-1:', error);
      throw error;
    }
  }
}

// Сервис для работы с заявками на консультацию с юристом
class FeedbackLawyerConsultationApiService {
  async getApplications(
    limit?: number,
    offset?: number,
    status?: string,
    userId?: string,
    userSearch?: string
  ) {
    try {
      return await FeedbackLawyerConsultationService.listLawyerConsultationApplicationsApiFeedbackLawyerConsultationGet(
        status as FeedbackApplicationStatus | null,
        userId,
        userSearch,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявок на консультацию юриста:', error);
      throw error;
    }
  }

  async getApplication(applicationId: string) {
    try {
      return await FeedbackLawyerConsultationService.getLawyerConsultationApplicationApiFeedbackLawyerConsultationApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявки на консультацию юриста:', error);
      throw error;
    }
  }

  async updateStatus(applicationId: string, status: FeedbackApplicationStatus) {
    try {
      return await FeedbackLawyerConsultationService.updateLawyerConsultationApplicationStatusApiFeedbackLawyerConsultationApplicationIdStatusPut(
        applicationId,
        { status }
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при обновлении статуса заявки на консультацию юриста:', error);
      throw error;
    }
  }

  async createMyApplication(description?: string) {
    try {
      const requestBody: CreateUserFeedbackRequest = { description: description || null };
      return await FeedbackLawyerConsultationService.createMyLawyerConsultationApplicationApiFeedbackLawyerConsultationMePost(
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при создании моей заявки на консультацию юриста:', error);
      throw error;
    }
  }

  async getMyApplications(status?: string, limit?: number, offset?: number) {
    try {
      return await FeedbackLawyerConsultationService.listMyLawyerConsultationApplicationsApiFeedbackLawyerConsultationMeGet(
        status as FeedbackApplicationStatus | null,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моих заявок на консультацию юриста:', error);
      throw error;
    }
  }

  async getMyApplication(applicationId: string) {
    try {
      return await FeedbackLawyerConsultationService.getMyLawyerConsultationApplicationApiFeedbackLawyerConsultationMeApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моей заявки на консультацию юриста:', error);
      throw error;
    }
  }
}

// Сервис для работы с заявками реферальной программы
class HiringReferralsApiService {
  async getApplications(
    limit?: number,
    offset?: number,
    status?: string,
    userId?: string,
    userSearch?: string,
    candidateName?: string,
    directionId?: string
  ) {
    try {
      return await HiringReferralsService.listReferralApplicationsApiHiringReferralsGet(
        status as ReferralApplicationStatus | null,
        userId,
        userSearch,
        candidateName,
        directionId,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявок реферальной программы:', error);
      throw error;
    }
  }

  async getApplication(applicationId: string) {
    try {
      return await HiringReferralsService.getReferralApplicationApiHiringReferralsApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявки реферальной программы:', error);
      throw error;
    }
  }

  async getApplicationFileUrl(applicationId: string, expiresIn?: number) {
    try {
      return await HiringReferralsService.getReferralApplicationFileUrlApiHiringReferralsApplicationIdFileUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL файла резюме:', error);
      throw error;
    }
  }

  async updateStatus(applicationId: string, status: ReferralApplicationStatus) {
    try {
      return await HiringReferralsService.updateReferralApplicationStatusEndpointApiHiringReferralsApplicationIdStatusPut(
        applicationId,
        { status }
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при обновлении статуса заявки реферальной программы:', error);
      throw error;
    }
  }

  async createMyApplication(
    directionId: string,
    candidateName: string,
    comment?: string | null,
    resumeType?: ResumeType,
    resumeFileUrl?: string | null,
    resumeFileName?: string | null,
    resumeUrl?: string | null
  ) {
    try {
      const requestBody: CreateUserReferralApplicationRequest = {
        direction_id: directionId,
        candidate_name: candidateName,
        comment: comment || null,
        resume_type: resumeType,
        resume_file_url: resumeFileUrl || null,
        resume_file_name: resumeFileName || null,
        resume_url: resumeUrl || null,
      };
      return await HiringReferralsService.createMyReferralApplicationApiHiringReferralsMePost(
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при создании моей заявки реферальной программы:', error);
      throw error;
    }
  }

  async getMyApplications(status?: string, limit?: number, offset?: number) {
    try {
      return await HiringReferralsService.listMyReferralApplicationsApiHiringReferralsMeGet(
        status as ReferralApplicationStatus | null,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моих заявок реферальной программы:', error);
      throw error;
    }
  }

  async getMyApplication(applicationId: string) {
    try {
      return await HiringReferralsService.getMyReferralApplicationApiHiringReferralsMeApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моей заявки реферальной программы:', error);
      throw error;
    }
  }

  async uploadResume(file: File) {
    try {
      const formData: Body_upload_my_resume_api_hiring_referrals_upload_temp_resume_post = {
        file: file,
      };
      return await HiringReferralsService.uploadMyResumeApiHiringReferralsUploadTempResumePost(
        formData
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при загрузке резюме:', error);
      throw error;
    }
  }
}

// Сервис для работы с направлениями рекрутинга
class HiringDirectionsApiService {
  async getDirections() {
    try {
      return await HiringDirectionsService.listRecruitingDirectionsApiHiringDirectionsGet();
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении направлений рекрутинга:', error);
      throw error;
    }
  }

  async getDirection(directionId: string) {
    try {
      return await HiringDirectionsService.getDirectionApiHiringDirectionsDirectionIdGet(
        directionId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении направления рекрутинга:', error);
      throw error;
    }
  }

  async createDirection(name: string, description?: string | null, isActive?: boolean) {
    try {
      return await HiringDirectionsService.createDirectionApiHiringDirectionsPost({
        name,
        description,
        is_active: isActive ?? true,
      });
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при создании направления рекрутинга:', error);
      throw error;
    }
  }

  async updateDirection(directionId: string, name?: string, description?: string | null) {
    try {
      const requestBody: UpdateRecruitingDirectionRequest = {};
      if (name !== undefined) {
        requestBody.name = name ?? null;
      }
      if (description !== undefined) {
        requestBody.description = description ?? null;
      }
      return await HiringDirectionsService.updateDirectionApiHiringDirectionsDirectionIdPut(
        directionId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при обновлении направления рекрутинга:', error);
      throw error;
    }
  }

  async activateDirection(directionId: string) {
    try {
      return await HiringDirectionsService.activateDirectionApiHiringDirectionsDirectionIdActivatePost(
        directionId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при активации направления рекрутинга:', error);
      throw error;
    }
  }

  async deactivateDirection(directionId: string) {
    try {
      return await HiringDirectionsService.deactivateDirectionApiHiringDirectionsDirectionIdDeactivatePost(
        directionId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при деактивации направления рекрутинга:', error);
      throw error;
    }
  }
}

// Сервис для работы с заявками на отпуск
class VacationApplicationsApiService {
  async getApplications(
    limit?: number,
    offset?: number,
    status?: string,
    userId?: string,
    userSearch?: string,
    applicationType?: string
  ) {
    try {
      return await VacationApplicationsService.listVacationApplicationsApiVacationApplicationsGet(
        status as VacationApplicationStatus | null,
        userId,
        userSearch,
        applicationType as any,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявок на отпуск:', error);
      throw error;
    }
  }

  async getApplication(applicationId: string) {
    try {
      return await VacationApplicationsService.getVacationApplicationApiVacationApplicationsApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявки на отпуск:', error);
      throw error;
    }
  }

  async getApplicationFileUrl(applicationId: string, expiresIn?: number) {
    try {
      return await VacationApplicationsService.getVacationApplicationFileUrlApiVacationApplicationsApplicationIdFileUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL файла заявки на отпуск:', error);
      throw error;
    }
  }

  async revertToOrderSigning(applicationId: string) {
    try {
      return await VacationApplicationsService.revertVacationToOrderSigningEndpointApiVacationApplicationsApplicationIdRevertToOrderSigningPost(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при возврате заявки на подписание приказа:', error);
      throw error;
    }
  }

  async updateVacationDates(applicationId: string, startDate: string, endDate: string) {
    try {
      const requestBody: UpdateVacationApplicationDatesRequest = {
        start_date: startDate,
        end_date: endDate,
      };
      return await VacationApplicationsService.updateVacationApplicationDatesEndpointApiVacationApplicationsApplicationIdDatesPut(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при обновлении дат отпуска:', error);
      throw error;
    }
  }

  async notifyPlanned(applicationId: string) {
    try {
      return await VacationApplicationsService.sendVacationPlannedNotificationEndpointApiVacationApplicationsApplicationIdNotifyPlannedPost(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      if (error instanceof ApiError && error.status === 400) {
        const errorCode = error.body?.code as string | undefined;
        if (errorCode === 'vacation_application_dates_not_specified') {
          throw new Error(
            'Не установлены даты отпуска. Пожалуйста, укажите даты перед отправкой нотификации.'
          );
        }
      }
      console.error('Ошибка при отправке нотификации о запланированном отпуске:', error);
      throw error;
    }
  }

  async getMyApplications(type?: VacationApplicationType, limit?: number, offset?: number) {
    try {
      return await VacationApplicationsService.listMyVacationApplicationsApiVacationApplicationsMeGet(
        type ?? null,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моих заявок на отпуск:', error);
      throw error;
    }
  }

  async getMyApplication(applicationId: string) {
    try {
      return await VacationApplicationsService.getMyVacationApplicationApiVacationApplicationsMeApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моей заявки на отпуск:', error);
      throw error;
    }
  }

  async getMyApplicationFileUrl(applicationId: string, expiresIn?: number) {
    try {
      return await VacationApplicationsService.getMyVacationApplicationFileUrlApiVacationApplicationsMeApplicationIdFileUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL файла моей заявки на отпуск:', error);
      throw error;
    }
  }

  async createMyApplication(
    type: VacationApplicationType,
    fileUrl: string,
    startDate: string,
    endDate: string,
    fileName?: string
  ) {
    try {
      const requestBody: CreateMyVacationApplicationRequest = {
        type,
        start_date: startDate,
        end_date: endDate,
        file_url: fileUrl,
        file_name: fileName ?? null,
      };
      return await VacationApplicationsService.createMyVacationApplicationApiVacationApplicationsMePost(
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при создании моей заявки на отпуск:', error);
      throw error;
    }
  }

  async uploadTempFile(file: File) {
    try {
      const formData: Body_upload_temp_file_api_vacation_applications_upload_temp_post = { file };
      return await VacationApplicationsService.uploadTempFileApiVacationApplicationsUploadTempPost(
        formData
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при загрузке временного файла:', error);
      throw error;
    }
  }

  async getTemplate(type?: VacationApplicationType) {
    try {
      return await VacationApplicationsService.getApplicationTemplateApiVacationApplicationsTemplateGet(
        type ?? VacationApplicationType.PAID
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении шаблона заявления на отпуск:', error);
      throw error;
    }
  }

  async uploadOrder(applicationId: string, fileUrl: string, fileName?: string) {
    try {
      const requestBody: UploadVacationOrderRequest = {
        file_url: fileUrl,
        file_name: fileName ?? null,
      };
      return await VacationApplicationsService.uploadVacationOrderEndpointApiVacationApplicationsApplicationIdOrderPost(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при загрузке приказа:', error);
      throw error;
    }
  }

  async uploadSignedOrder(applicationId: string, fileUrl: string, fileName?: string) {
    try {
      const requestBody: UploadVacationSignedOrderRequest = {
        file_url: fileUrl,
        file_name: fileName ?? null,
      };
      return await VacationApplicationsService.uploadVacationSignedOrderEndpointApiVacationApplicationsApplicationIdSignedOrderPost(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при загрузке подписанного приказа:', error);
      throw error;
    }
  }

  async getOrderUrl(applicationId: string, expiresIn?: number) {
    try {
      return await VacationApplicationsService.getVacationOrderFileUrlApiVacationApplicationsApplicationIdOrderUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL приказа:', error);
      throw error;
    }
  }

  async getSignedOrderUrl(applicationId: string, expiresIn?: number) {
    try {
      return await VacationApplicationsService.getVacationSignedOrderFileUrlApiVacationApplicationsApplicationIdSignedOrderUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL подписанного приказа:', error);
      throw error;
    }
  }

  async getMyOrderUrl(applicationId: string, expiresIn?: number) {
    try {
      return await VacationApplicationsService.getMyVacationOrderFileUrlApiVacationApplicationsMeApplicationIdOrderUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL приказа (моей заявки):', error);
      throw error;
    }
  }

  async getMySignedOrderUrl(applicationId: string, expiresIn?: number) {
    try {
      return await VacationApplicationsService.getMyVacationSignedOrderFileUrlApiVacationApplicationsMeApplicationIdSignedOrderUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL подписанного приказа (моей заявки):', error);
      throw error;
    }
  }

  async uploadMySignedOrder(applicationId: string, fileUrl: string, fileName?: string) {
    try {
      const requestBody: UploadMyVacationSignedOrderRequest = {
        file_url: fileUrl,
        file_name: fileName ?? null,
      };
      return await VacationApplicationsService.uploadMyVacationSignedOrderApiVacationApplicationsMeApplicationIdSignedOrderPost(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при загрузке подписанного приказа (моей заявки):', error);
      throw error;
    }
  }

  async notifyOrderSigning(applicationId: string) {
    try {
      return await VacationApplicationsService.sendVacationOrderSigningNotificationEndpointApiVacationApplicationsApplicationIdNotifyOrderSigningPost(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при отправке нотификации о подписании приказа:', error);
      throw error;
    }
  }

  async confirmMyVacation(applicationId: string) {
    try {
      return await VacationApplicationsService.confirmMyVacationApiVacationApplicationsMeApplicationIdConfirmPost(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при подтверждении отпуска:', error);
      throw error;
    }
  }

  async cancelMyVacation(applicationId: string) {
    try {
      return await VacationApplicationsService.cancelMyVacationApiVacationApplicationsMeApplicationIdCancelPost(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при отмене отпуска:', error);
      throw error;
    }
  }

  async rescheduleMyVacation(
    applicationId: string,
    startDate: string,
    endDate: string,
    fileUrl: string,
    fileName?: string
  ) {
    try {
      const requestBody: RescheduleMyVacationRequest = {
        start_date: startDate,
        end_date: endDate,
        file_url: fileUrl,
        file_name: fileName ?? null,
      };
      return await VacationApplicationsService.rescheduleMyVacationApiVacationApplicationsMeApplicationIdReschedulePost(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при переносе отпуска:', error);
      throw error;
    }
  }
}

// Сервис для работы с заявками на выходной (Day Off)
class DayOffApplicationsApiService {
  async getApplications(
    limit?: number,
    offset?: number,
    status?: string,
    userId?: string,
    userSearch?: string,
    applicationType?: string
  ) {
    try {
      return await DayOffApplicationsService.listDayOffApplicationsApiVacationDayOffApplicationsGet(
        status as DayOffApplicationStatus | null,
        userId,
        userSearch,
        applicationType as any,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявок на выходной:', error);
      throw error;
    }
  }

  async getApplication(applicationId: string) {
    try {
      return await DayOffApplicationsService.getDayOffApplicationApiVacationDayOffApplicationsApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявки на выходной:', error);
      throw error;
    }
  }

  async getApplicationFileUrl(applicationId: string, expiresIn?: number) {
    try {
      return await DayOffApplicationsService.getDayOffApplicationFileUrlApiVacationDayOffApplicationsApplicationIdFileUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL файла заявки на выходной:', error);
      throw error;
    }
  }

  async revertToOrderSigning(applicationId: string) {
    try {
      return await DayOffApplicationsService.revertDayOffToOrderSigningEndpointApiVacationDayOffApplicationsApplicationIdRevertToOrderSigningPost(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при возврате заявки на выходной на подписание приказа:', error);
      throw error;
    }
  }

  async getMyApplications(type?: DayOffApplicationType, limit?: number, offset?: number) {
    try {
      return await DayOffApplicationsService.listMyDayOffApplicationsApiVacationDayOffApplicationsMeGet(
        type ?? null,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моих заявок на выходной:', error);
      throw error;
    }
  }

  async getMyApplication(applicationId: string) {
    try {
      return await DayOffApplicationsService.getMyDayOffApplicationApiVacationDayOffApplicationsMeApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моей заявки на выходной:', error);
      throw error;
    }
  }

  async getMyApplicationFileUrl(applicationId: string, expiresIn?: number) {
    try {
      return await DayOffApplicationsService.getMyDayOffApplicationFileUrlApiVacationDayOffApplicationsMeApplicationIdFileUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL файла моей заявки на выходной:', error);
      throw error;
    }
  }

  async createMyApplication(
    type: DayOffApplicationType,
    fileUrl: string,
    startDate: string,
    endDate: string,
    reason?: string,
    fileName?: string
  ) {
    try {
      const requestBody: CreateMyDayOffApplicationRequest = {
        type,
        start_date: startDate,
        end_date: endDate,
        file_url: fileUrl,
        file_name: fileName ?? null,
        reason: reason ?? null,
      };
      return await DayOffApplicationsService.createMyDayOffApplicationApiVacationDayOffApplicationsMePost(
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при создании моей заявки на выходной:', error);
      throw error;
    }
  }

  async uploadTempFile(file: File) {
    try {
      const formData: Body_upload_temp_file_api_vacation_day_off_applications_upload_temp_post = {
        file,
      };
      return await DayOffApplicationsService.uploadTempFileApiVacationDayOffApplicationsUploadTempPost(
        formData
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при загрузке временного файла:', error);
      throw error;
    }
  }

  async getTemplate(type?: DayOffApplicationType) {
    try {
      return await DayOffApplicationsService.getDayOffApplicationTemplateApiVacationDayOffApplicationsTemplateGet(
        type ?? DayOffApplicationType.PAID
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении шаблона заявления на выходной:', error);
      throw error;
    }
  }

  async getOrderUrl(applicationId: string, expiresIn?: number) {
    try {
      return await DayOffApplicationsService.getDayOffOrderFileUrlApiVacationDayOffApplicationsApplicationIdOrderUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL приказа:', error);
      throw error;
    }
  }

  async getSignedOrderUrl(applicationId: string, expiresIn?: number) {
    try {
      return await DayOffApplicationsService.getDayOffSignedOrderFileUrlApiVacationDayOffApplicationsApplicationIdSignedOrderUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL подписанного приказа:', error);
      throw error;
    }
  }

  async uploadOrder(applicationId: string, fileUrl: string, fileName?: string) {
    try {
      const requestBody: UploadDayOffOrderRequest = {
        file_url: fileUrl,
        file_name: fileName ?? null,
      };
      return await DayOffApplicationsService.uploadDayOffOrderEndpointApiVacationDayOffApplicationsApplicationIdOrderPost(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при загрузке приказа:', error);
      throw error;
    }
  }

  async uploadSignedOrder(applicationId: string, fileUrl: string, fileName?: string) {
    try {
      const requestBody: UploadDayOffSignedOrderRequest = {
        file_url: fileUrl,
        file_name: fileName ?? null,
      };
      return await DayOffApplicationsService.uploadDayOffSignedOrderEndpointApiVacationDayOffApplicationsApplicationIdSignedOrderPost(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при загрузке подписанного приказа:', error);
      throw error;
    }
  }

  async getMyOrderUrl(applicationId: string, expiresIn?: number) {
    try {
      return await DayOffApplicationsService.getMyDayOffOrderFileUrlApiVacationDayOffApplicationsMeApplicationIdOrderUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении URL приказа (моей заявки на выходной):', error);
      throw error;
    }
  }

  async getMySignedOrderUrl(applicationId: string, expiresIn?: number) {
    try {
      return await DayOffApplicationsService.getMyDayOffSignedOrderFileUrlApiVacationDayOffApplicationsMeApplicationIdSignedOrderUrlGet(
        applicationId,
        expiresIn
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error(
        'Ошибка при получении URL подписанного приказа (моей заявки на выходной):',
        error
      );
      throw error;
    }
  }

  async uploadMySignedOrder(applicationId: string, fileUrl: string, fileName?: string) {
    try {
      const requestBody: UploadMyDayOffSignedOrderRequest = {
        file_url: fileUrl,
        file_name: fileName ?? null,
      };
      return await DayOffApplicationsService.uploadMyDayOffSignedOrderApiVacationDayOffApplicationsMeApplicationIdSignedOrderPost(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при загрузке подписанного приказа (моей заявки на выходной):', error);
      throw error;
    }
  }

  async confirmMyDayOff(applicationId: string) {
    try {
      return await DayOffApplicationsService.confirmMyDayOffApiVacationDayOffApplicationsMeApplicationIdConfirmPost(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при подтверждении выходного:', error);
      throw error;
    }
  }

  async cancelMyDayOff(applicationId: string) {
    try {
      return await DayOffApplicationsService.cancelMyDayOffApiVacationDayOffApplicationsMeApplicationIdCancelPost(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при отмене выходного:', error);
      throw error;
    }
  }

  async notifyPlanned(applicationId: string) {
    try {
      return await DayOffApplicationsService.sendDayoffPlannedNotificationEndpointApiVacationDayOffApplicationsApplicationIdNotifyPlannedPost(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при отправке нотификации о запланированном выходном:', error);
      throw error;
    }
  }
}

// Сервис для работы с заявками на больничный
class SickLeaveApplicationsApiService {
  async getApplications(
    limit?: number,
    offset?: number,
    status?: string,
    userId?: string,
    userSearch?: string,
    startDateFrom?: string,
    startDateTo?: string,
    endDateFrom?: string,
    endDateTo?: string
  ) {
    try {
      return await SickLeaveApplicationsService.listSickLeaveApplicationsApiSickLeaveApplicationsGet(
        status as SickLeaveApplicationStatus | null,
        userId,
        userSearch,
        startDateFrom,
        startDateTo,
        endDateFrom,
        endDateTo,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявок на больничный:', error);
      throw error;
    }
  }

  async getApplication(applicationId: string) {
    try {
      return await SickLeaveApplicationsService.getSickLeaveApplicationApiSickLeaveApplicationsApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении заявки на больничный:', error);
      throw error;
    }
  }

  async updateStatus(applicationId: string, status: SickLeaveApplicationStatus) {
    try {
      return await SickLeaveApplicationsService.updateSickLeaveApplicationStatusEndpointApiSickLeaveApplicationsApplicationIdStatusPut(
        applicationId,
        { status }
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при обновлении статуса заявки на больничный:', error);
      throw error;
    }
  }

  async getMyApplications(status?: SickLeaveApplicationStatus, limit?: number, offset?: number) {
    try {
      return await SickLeaveApplicationsService.listMySickLeaveApplicationsApiSickLeaveApplicationsMeGet(
        status ?? null,
        limit,
        offset
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моих заявок на больничный:', error);
      throw error;
    }
  }

  async getMyApplication(applicationId: string) {
    try {
      return await SickLeaveApplicationsService.getMySickLeaveApplicationApiSickLeaveApplicationsMeApplicationIdGet(
        applicationId
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при получении моей заявки на больничный:', error);
      throw error;
    }
  }

  async createMyApplication(startDate: string, endDate: string) {
    try {
      const requestBody: CreateMySickLeaveApplicationRequest = {
        start_date: startDate,
        end_date: endDate,
      };
      return await SickLeaveApplicationsService.createMySickLeaveApplicationApiSickLeaveApplicationsMePost(
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при создании моей заявки на больничный:', error);
      throw error;
    }
  }

  async extendMyApplication(applicationId: string, endDate: string) {
    try {
      const requestBody: UpdateSickLeaveEndDateRequest = {
        end_date: endDate,
      };
      return await SickLeaveApplicationsService.extendMySickLeaveApplicationApiSickLeaveApplicationsMeApplicationIdExtendPut(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при продлении больничного:', error);
      throw error;
    }
  }

  async adminExtendApplication(applicationId: string, endDate: string) {
    try {
      const requestBody: UpdateSickLeaveEndDateRequest = {
        end_date: endDate,
      };
      return await SickLeaveApplicationsService.adminExtendSickLeaveApplicationApiSickLeaveApplicationsApplicationIdExtendPut(
        applicationId,
        requestBody
      );
    } catch (error) {
      handleGlobalAuthError(error);
      console.error('Ошибка при продлении больничного (админ):', error);
      throw error;
    }
  }
}

// Сервис для восстановления пароля
class PasswordRecoveryApiService {
  async initRecovery(email: string) {
    try {
      return await PasswordAuthRecoveryService.initPasswordAuthRecoveryEndpointApiAuthPasswordAuthRecoveryPost(
        { email }
      );
    } catch (error) {
      console.error('Ошибка при инициализации восстановления пароля:', error);
      throw error;
    }
  }

  async completeRecovery(token: string, newPassword: string, newPasswordConfirm: string) {
    try {
      return await PasswordAuthRecoveryService.completePasswordAuthRecoveryEndpointApiAuthPasswordAuthRecoveryCompletePost(
        {
          token,
          new_password: newPassword,
          new_password_confirm: newPasswordConfirm,
        }
      );
    } catch (error) {
      console.error('Ошибка при завершении восстановления пароля:', error);
      throw error;
    }
  }
}

// Экспортируем экземпляры сервисов
export const authApiService = new AuthApiService();
export const usersApiService = new UsersApiService();
export const feedbackHrOneOnOneApiService = new FeedbackHrOneOnOneApiService();
export const feedbackLawyerConsultationApiService = new FeedbackLawyerConsultationApiService();
export const hiringReferralsApiService = new HiringReferralsApiService();
export const hiringDirectionsApiService = new HiringDirectionsApiService();
export const vacationApplicationsApiService = new VacationApplicationsApiService();
export const dayOffApplicationsApiService = new DayOffApplicationsApiService();
export const sickLeaveApplicationsApiService = new SickLeaveApplicationsApiService();
export const passwordRecoveryApiService = new PasswordRecoveryApiService();

// Экспортируем классы для возможности создания собственных экземпляров
export {
  AuthApiService,
  UsersApiService,
  FeedbackHrOneOnOneApiService,
  FeedbackLawyerConsultationApiService,
  HiringReferralsApiService,
  HiringDirectionsApiService,
  VacationApplicationsApiService,
  DayOffApplicationsApiService,
  SickLeaveApplicationsApiService,
  PasswordRecoveryApiService,
};
